import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  HealthAndSafety as HealthIcon,
  ToggleOn as ToggleIcon,
  Description as LogIcon,
  Backup as BackupIcon,
  Speed as MetricsIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoCircleIcon,
  Storage as StorageIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Security as SecurityIcon,
  Apps as AppsIcon,
  SettingsSuggest as EnvironmentIcon,
  Timeline as TimelineIcon,
  CloudDownload as DownloadIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ padding: '24px 0' }}>
    {value === index && children}
  </div>
);

interface SystemConfiguration {
  database: {
    url: string;
    driver: string;
    username: string;
    poolMinSize: number;
    poolMaxSize: number;
    connectionTimeout: number;
    sslEnabled: boolean;
    status: string;
    statusColor: string;
  };
  email: {
    host: string;
    port: number;
    username: string;
    fromEmail: string;
    fromName: string;
    authEnabled: boolean;
    tlsEnabled: boolean;
    active: boolean;
    status: string;
    statusColor: string;
  };
  sms: {
    provider: string;
    accountSid: string;
    fromNumber: string;
    active: boolean;
    status: string;
    statusColor: string;
  };
  security: {
    jwtExpiration: number;
    refreshTokenExpiration: number;
    maxLoginAttempts: number;
    lockoutDuration: number;
    twoFactorEnabled: boolean;
    passwordExpiryEnabled: boolean;
    passwordExpiryDays: number;
    passwordMinLength: number;
    passwordRequireUppercase: boolean;
    passwordRequireLowercase: boolean;
    passwordRequireDigits: boolean;
    passwordRequireSpecial: boolean;
  };
  storage: {
    type: string;
    endpoint: string;
    bucket: string;
    maxSize: number;
    allowedTypes: string[];
    status: string;
    statusColor: string;
  };
  app: {
    appName: string;
    appVersion: string;
    environment: string;
    timezone: string;
    language: string;
    sessionTimeout: number;
    maintenanceMode: boolean;
    maintenanceMessage: string;
  };
}

interface FeatureToggle {
  id: number;
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  category: string;
  updatedAt: string;
  updatedBy: string;
}

interface SystemLog {
  id: number;
  timestamp: string;
  level: string;
  levelColor: string;
  logger: string;
  message: string;
  thread: string;
  host: string;
}

interface BackupConfig {
  enabled: boolean;
  schedule: string;
  retention: string;
  storagePath: string;
  compressEnabled: boolean;
  encryptEnabled: boolean;
  lastBackup: string;
  nextBackup: string;
  backupSize: number;
  status: string;
  statusColor: string;
}

interface BackupHistory {
  id: number;
  timestamp: string;
  type: string;
  status: string;
  statusColor: string;
  size: number;
  duration: string;
  backupBy: string;
  location: string;
}

interface SystemHealth {
  status: string;
  statusColor: string;
  uptime: string;
  startTime: string;
  cpuUsage: number;
  memoryUsage: number;
  diskUsage: number;
  components: {
    name: string;
    status: string;
    statusColor: string;
    message: string;
    details: Record<string, any>;
  }[];
}

interface SystemMetrics {
  totalUsers: number;
  activeUsersToday: number;
  totalAppointments: number;
  appointmentsToday: number;
  totalDoctors: number;
  totalOfficers: number;
  avgResponseTime: number;
  totalRequests: number;
  failedRequests: number;
  uptimePercentage: number;
  dailyMetrics: {
    date: string;
    requests: number;
    errors: number;
    avgResponseTime: number;
    uniqueUsers: number;
  }[];
}

const ConfigurationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Configuration state
  const [configuration, setConfiguration] = useState<SystemConfiguration | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [featureToggles, setFeatureToggles] = useState<FeatureToggle[]>([]);
  const [systemLogs, setSystemLogs] = useState<SystemLog[]>([]);
  const [backupConfig, setBackupConfig] = useState<BackupConfig | null>(null);
  const [backupHistory, setBackupHistory] = useState<BackupHistory[]>([]);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);

  // Filter state
  const [logLevelFilter, setLogLevelFilter] = useState('');
  const [logSearchFilter, setLogSearchFilter] = useState('');

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<string>('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [configRes, healthRes, featuresRes, logsRes, backupRes, historyRes, metricsRes] = await Promise.all([
        axios.get('/api/v1/admin/configuration'),
        axios.get('/api/v1/admin/configuration/health'),
        axios.get('/api/v1/admin/configuration/features'),
        axios.get('/api/v1/admin/configuration/logs'),
        axios.get('/api/v1/admin/configuration/backup'),
        axios.get('/api/v1/admin/configuration/backup/history'),
        axios.get('/api/v1/admin/configuration/metrics'),
      ]);

      setConfiguration(configRes.data.data);
      setHealth(healthRes.data.data);
      setFeatureToggles(featuresRes.data.data);
      setSystemLogs(logsRes.data.data);
      setBackupConfig(backupRes.data.data);
      setBackupHistory(historyRes.data.data);
      setMetrics(metricsRes.data.data);
    } catch (error) {
      console.error('Error fetching configuration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureToggle = async (key: string, enabled: boolean) => {
    try {
      await axios.put('/api/v1/admin/configuration/features', { key, enabled });
      setFeatureToggles(toggles =>
        toggles.map(t => (t.key === key ? { ...t, enabled } : t))
      );
    } catch (error) {
      console.error('Error updating feature toggle:', error);
    }
  };

  const handleTriggerBackup = async () => {
    try {
      await axios.post('/api/v1/admin/configuration/backup/trigger');
      fetchAllData();
    } catch (error) {
      console.error('Error triggering backup:', error);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'UP':
      case 'SUCCESS':
      case 'ACTIVE':
        return <CheckIcon sx={{ color: '#10b981' }} />;
      case 'DOWN':
      case 'FAILED':
        return <ErrorIcon sx={{ color: '#ef4444' }} />;
      case 'WARNING':
        return <WarningIcon sx={{ color: '#f59e0b' }} />;
      default:
        return <InfoCircleIcon sx={{ color: '#3b82f6' }} />;
    }
  };

  const getLevelChip = (level: string, color: string) => {
    const levelColors: Record<string, { bg: string; text: string }> = {
      ERROR: { bg: '#fef2f2', text: '#dc2626' },
      WARN: { bg: '#fffbeb', text: '#d97706' },
      INFO: { bg: '#eff6ff', text: '#2563eb' },
      DEBUG: { bg: '#f5f3ff', text: '#7c3aed' },
    };
    const c = levelColors[level] || { bg: '#f8fafc', text: '#64748b' };
    return (
      <Chip
        label={level}
        size="small"
        sx={{ bgcolor: c.bg, color: c.text, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
          Configuration & System Management
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
          Manage system configuration, features, backups, and monitor system health
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: '1px solid #e2e8f0', px: 2 }}
        >
          <Tab icon={<SettingsIcon />} label="Configuration" iconPosition="start" />
          <Tab icon={<HealthIcon />} label="System Health" iconPosition="start" />
          <Tab icon={<ToggleIcon />} label="Features" iconPosition="start" />
          <Tab icon={<LogIcon />} label="Logs" iconPosition="start" />
          <Tab icon={<BackupIcon />} label="Backups" iconPosition="start" />
          <Tab icon={<MetricsIcon />} label="Metrics" iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 1: Configuration */}
          <TabPanel value={activeTab} index={0}>
            {configuration && (
              <Grid container spacing={3}>
                {/* Database Configuration */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'transparent' , color: '#2563eb' }}>
                            <StorageIcon />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Database
                          </Typography>
                        </Box>
                        {getStatusIcon(configuration.database.status)}
                      </Box>
                      <List dense>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Driver" secondary={configuration.database.driver} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Pool Size" secondary={`${configuration.database.poolMinSize} - ${configuration.database.poolMaxSize}`} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Connection Timeout" secondary={`${configuration.database.connectionTimeout}ms`} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="SSL Enabled" secondary={configuration.database.sslEnabled ? 'Yes' : 'No'} />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Email Configuration */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'transparent' , color: '#d97706' }}>
                            <EmailIcon />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Email Service
                          </Typography>
                        </Box>
                        {getStatusIcon(configuration.email.status)}
                      </Box>
                      <List dense>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Host" secondary={`${configuration.email.host}:${configuration.email.port}`} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="From" secondary={configuration.email.fromEmail} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="TLS Enabled" secondary={configuration.email.tlsEnabled ? 'Yes' : 'No'} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Active" secondary={configuration.email.active ? 'Yes' : 'No'} />
                        </ListItem>
                      </List>
                      <Button variant="outlined" size="small" sx={{ mt: 1 }} startIcon={<EditIcon />}>
                        Configure
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* SMS Configuration */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'transparent' , color: '#16a34a' }}>
                            <SmsIcon />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            SMS Service
                          </Typography>
                        </Box>
                        {getStatusIcon(configuration.sms.status)}
                      </Box>
                      <List dense>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Provider" secondary={configuration.sms.provider} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="From Number" secondary={configuration.sms.fromNumber || 'Not configured'} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Active" secondary={configuration.sms.active ? 'Yes' : 'No'} />
                        </ListItem>
                      </List>
                      <Button variant="outlined" size="small" sx={{ mt: 1 }} startIcon={<EditIcon />}>
                        Configure
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Security Configuration */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'transparent' , color: '#db2777' }}>
                            <SecurityIcon />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Security
                          </Typography>
                        </Box>
                        <Chip label="Active" size="small" sx={{ bgcolor: 'transparent' , color: '#16a34a' }} />
                      </Box>
                      <List dense>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="JWT Expiration" secondary={`${configuration.security.jwtExpiration / 3600000} hours`} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Max Login Attempts" secondary={configuration.security.maxLoginAttempts} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="2FA Enabled" secondary={configuration.security.twoFactorEnabled ? 'Yes' : 'No'} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Password Min Length" secondary={configuration.security.passwordMinLength} />
                        </ListItem>
                      </List>
                      <Button variant="outlined" size="small" sx={{ mt: 1 }} startIcon={<EditIcon />}>
                        Configure
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Storage Configuration */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'transparent' , color: '#4f46e5' }}>
                            <StorageIcon />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Storage
                          </Typography>
                        </Box>
                        {getStatusIcon(configuration.storage.status)}
                      </Box>
                      <List dense>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Type" secondary={configuration.storage.type} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Max File Size" secondary={formatBytes(configuration.storage.maxSize)} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Allowed Types" secondary={configuration.storage.allowedTypes.join(', ')} />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* App Configuration */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'transparent' , color: '#15803d' }}>
                            <AppsIcon />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Application
                          </Typography>
                        </Box>
                        <Chip label={configuration.app.environment?.toUpperCase()} size="small" sx={{ bgcolor: 'transparent' , color: '#1d4ed8' }} />
                      </Box>
                      <List dense>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Version" secondary={configuration.app.appVersion} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Timezone" secondary={configuration.app.timezone} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Session Timeout" secondary={`${configuration.app.sessionTimeout}s`} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Maintenance Mode" secondary={configuration.app.maintenanceMode ? 'Yes' : 'No'} />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* Tab 2: System Health */}
          <TabPanel value={activeTab} index={1}>
            {health && (
              <Grid container spacing={3}>
                {/* Overall Status */}
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: health.statusColor?.startsWith('#') ? `${health.statusColor}10` : '#f8fafc' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {getStatusIcon(health.status)}
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              System Status: {health.status}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                              Uptime: {health.uptime} | Started: {health.startTime}
                            </Typography>
                          </Box>
                        </Box>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAllData}>
                          Refresh
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Resource Usage */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>CPU Usage</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: health.cpuUsage > 80 ? '#ef4444' : '#1e293b' }}>
                        {health.cpuUsage?.toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={health.cpuUsage || 0}
                        sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: 'transparent' , '& .MuiLinearProgress-bar': { bgcolor: health.cpuUsage > 80 ? '#ef4444' : '#2563eb' } }}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>Memory Usage</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: health.memoryUsage > 80 ? '#ef4444' : '#1e293b' }}>
                        {health.memoryUsage?.toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={health.memoryUsage || 0}
                        sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: 'transparent' , '& .MuiLinearProgress-bar': { bgcolor: health.memoryUsage > 80 ? '#ef4444' : '#16a34a' } }}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>Disk Usage</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: health.diskUsage > 80 ? '#ef4444' : '#1e293b' }}>
                        {health.diskUsage?.toFixed(1)}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={health.diskUsage || 0}
                        sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: 'transparent' , '& .MuiLinearProgress-bar': { bgcolor: health.diskUsage > 80 ? '#ef4444' : '#f59e0b' } }}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Component Health */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Component Health
                  </Typography>
                  <Grid container spacing={2}>
                    {health.components?.map((component, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {component.name}
                              </Typography>
                              <Chip
                                label={component.status}
                                size="small"
                                sx={{ bgcolor: `${component.statusColor}20`, color: component.statusColor, fontWeight: 600 }}
                              />
                            </Box>
                            <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                              {component.message}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Grid container spacing={1}>
                              {Object.entries(component.details || {}).map(([key, value]) => (
                                <Grid item xs={6} key={key}>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>{key}:</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{String(value)}</Typography>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* Tab 3: Feature Toggles */}
          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={2}>
              {featureToggles.map((feature) => (
                <Grid item xs={12} md={6} lg={4} key={feature.id}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {feature.name}
                        </Typography>
                        <Switch
                          checked={feature.enabled}
                          onChange={(e) => handleFeatureToggle(feature.key, e.target.checked)}
                          color="primary"
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                        {feature.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={feature.category} size="small" sx={{ bgcolor: 'transparent' , color: '#475569' }} />
                        <Chip
                          label={feature.enabled ? 'Enabled' : 'Disabled'}
                          size="small"
                          sx={{ bgcolor: feature.enabled ? '#dcfce7' : '#fee2e2', color: feature.enabled ? '#16a34a' : '#dc2626' }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1 }}>
                        Last updated: {feature.updatedBy} at {feature.updatedAt}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* Tab 4: System Logs */}
          <TabPanel value={activeTab} index={3}>
            {/* Filters */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Level</InputLabel>
                <Select
                  value={logLevelFilter}
                  label="Level"
                  onChange={(e) => setLogLevelFilter(e.target.value)}
                >
                  <MenuItem value="">All Levels</MenuItem>
                  <MenuItem value="ERROR">ERROR</MenuItem>
                  <MenuItem value="WARN">WARN</MenuItem>
                  <MenuItem value="INFO">INFO</MenuItem>
                  <MenuItem value="DEBUG">DEBUG</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                placeholder="Search logs..."
                value={logSearchFilter}
                onChange={(e) => setLogSearchFilter(e.target.value)}
                sx={{ minWidth: 250 }}
              />
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAllData}>
                Refresh
              </Button>
            </Box>

            {/* Logs Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Level</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Logger</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Thread</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {systemLogs
                    .filter(log => !logLevelFilter || log.level === logLevelFilter)
                    .filter(log => !logSearchFilter || log.message.toLowerCase().includes(logSearchFilter.toLowerCase()))
                    .slice(0, 50)
                    .map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {log.timestamp}
                        </TableCell>
                        <TableCell>{getLevelChip(log.level, log.levelColor)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {log.logger.split('.').pop()}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 400 }}>
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.message}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {log.thread}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Tab 5: Backups */}
          <TabPanel value={activeTab} index={4}>
            {backupConfig && (
              <Grid container spacing={3}>
                {/* Backup Configuration */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Backup Configuration
                      </Typography>
                      <List dense>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Status" secondary={backupConfig.enabled ? 'Enabled' : 'Disabled'} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Schedule" secondary={backupConfig.schedule} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Retention" secondary={backupConfig.retention} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Storage Path" secondary={backupConfig.storagePath} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Compression" secondary={backupConfig.compressEnabled ? 'Enabled' : 'Disabled'} />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText primary="Encryption" secondary={backupConfig.encryptEnabled ? 'Enabled' : 'Disabled'} />
                        </ListItem>
                      </List>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button variant="contained" startIcon={<PlayIcon />} onClick={handleTriggerBackup}>
                          Backup Now
                        </Button>
                        <Button variant="outlined" startIcon={<EditIcon />}>
                          Configure
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Backup Status */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Backup Status
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>Last Backup</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{backupConfig.lastBackup}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>Next Backup</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{backupConfig.nextBackup}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>Backup Size</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatBytes(backupConfig.backupSize)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>Status</Typography>
                          <Chip
                            label={backupConfig.status}
                            size="small"
                            sx={{ bgcolor: `${backupConfig.statusColor}20`, color: backupConfig.statusColor, fontWeight: 600 }}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Backup History */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Backup History
                  </Typography>
                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'transparent' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>By</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {backupHistory.map((backup) => (
                          <TableRow key={backup.id} hover>
                            <TableCell>{backup.timestamp}</TableCell>
                            <TableCell>
                              <Chip label={backup.type} size="small" sx={{ bgcolor: 'transparent' }} />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={backup.status}
                                size="small"
                                sx={{ bgcolor: `${backup.statusColor}20`, color: backup.statusColor }}
                              />
                            </TableCell>
                            <TableCell>{formatBytes(backup.size)}</TableCell>
                            <TableCell>{backup.duration}</TableCell>
                            <TableCell>{backup.backupBy}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{backup.location}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </TabPanel>

          {/* Tab 6: Metrics */}
          <TabPanel value={activeTab} index={5}>
            {metrics && (
              <Grid container spacing={3}>
                {/* KPI Cards */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Total Users</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {metrics.totalUsers}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#16a34a' }}>
                        +{metrics.activeUsersToday} active today
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Total Appointments</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {metrics.totalAppointments}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#16a34a' }}>
                        +{metrics.appointmentsToday} today
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Avg Response Time</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {metrics.avgResponseTime?.toFixed(1)}ms
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#16a34a' }}>
                        {metrics.uptimePercentage}% uptime
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Total Requests</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {metrics.totalRequests?.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: metrics.failedRequests > 0 ? '#ef4444' : '#16a34a' }}>
                        {metrics.failedRequests} failed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Daily Metrics Table */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Daily Metrics (Last 7 Days)
                  </Typography>
                  <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'transparent' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Requests</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Errors</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Avg Response</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Unique Users</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {metrics.dailyMetrics?.map((metric, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{metric.date}</TableCell>
                            <TableCell>{metric.requests?.toLocaleString()}</TableCell>
                            <TableCell>
                              <Chip
                                label={metric.errors}
                                size="small"
                                sx={{ bgcolor: metric.errors > 10 ? '#fee2e2' : '#dcfce7', color: metric.errors > 10 ? '#dc2626' : '#16a34a' }}
                              />
                            </TableCell>
                            <TableCell>{metric.avgResponseTime?.toFixed(1)}ms</TableCell>
                            <TableCell>{metric.uniqueUsers}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </TabPanel>
        </Box>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure {editingSection}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Configuration form would be displayed here.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfigurationPage;